import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const DailyMissionCard = () => {
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMission();
  }, []);

  const fetchMission = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scaly/daily-mission`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMission(data);
      }
    } catch (error) {
      console.error('Failed to fetch daily mission:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white animate-pulse">
        <div className="h-32 bg-slate-200 rounded"></div>
      </Card>
    );
  }

  if (!mission) return null;

  const progressPercentage = mission.target > 0 ? (mission.progress / mission.target) * 100 : 0;
  const isComplete = mission.completed || mission.progress >= mission.target;

  const getMissionRoute = (missionType) => {
    const routes = {
      grammar_mcq: '/practice/grammar_mcq',
      vocabulary_mcq: '/practice/vocabulary_mcq',
      grammar_cloze: '/practice/grammar_cloze',
      vocabulary_cloze: '/practice/vocabulary_cloze',
    };
    return routes[missionType] || '/practice';
  };

  return (
    <Card 
      className="p-6 rounded-2xl border-2 border-blue-400 bg-gradient-to-br from-blue-50 via-indigo-50 to-white relative overflow-hidden"
      data-testid="daily-mission-card"
    >
      {/* Decorative background pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full opacity-20 -mr-16 -mt-16"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-200 rounded-full opacity-20 -ml-12 -mb-12"></div>

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-2xl">
              {mission.mission_icon}
            </div>
            <div>
              <h3 className="font-heading font-bold text-slate-900 text-lg">
                Today's Scaly Mission
              </h3>
              <p className="text-xs text-slate-600">{mission.mission_title}</p>
            </div>
          </div>
          {isComplete && (
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold border border-green-200">
              ✓ Complete!
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-slate-700">
              Progress: {mission.progress} / {mission.target} questions
            </span>
            <span className="text-sm text-slate-600">{mission.estimated_time}</span>
          </div>
          <Progress value={progressPercentage} className="h-3" />
        </div>

        {/* Reward */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-600">Reward:</span>
            <span className="font-bold text-amber-600">{mission.reward}</span>
          </div>
          <Button
            onClick={() => navigate(getMissionRoute(mission.mission_type))}
            className={`${
              isComplete
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
            } text-white font-bold`}
            data-testid="daily-mission-start"
          >
            {isComplete ? 'Play Again' : 'Start Mission'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default DailyMissionCard;
